import type { CSSProperties } from "react";
import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { KaajActions, KaajCard, KaajHeading, KaajSectionShell } from "../../shared/kaaj-content";
import { RichTextHtml } from "../../RichTextHtml";
import type { IResourceHighlightBlockProps } from "./ResourceHighlightBlock.props";

const getImageStyle = (focalPoint?: { x: number; y: number } | null): CSSProperties | undefined => {
  if (!focalPoint) return undefined;

  return { objectPosition: `${focalPoint.x}% ${focalPoint.y}%` };
};

const ResourceDetail = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-2xl border border-[#D9E1FF] bg-[#FAFBFF] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]">
    <dt className="text-[0.68rem] font-extrabold uppercase tracking-[0.14em] text-[#4B5568]">
      {label}
    </dt>
    <dd className="mt-1 text-sm font-extrabold leading-5 text-[#151927] [overflow-wrap:anywhere]">
      {value}
    </dd>
  </div>
);

const ResourceMediaFrame = ({ image }: IResourceHighlightBlockProps) => {
  const Image = resolveKaajPrimitive("Image");
  const src = image?.defaultSrc;

  if (!src) {
    return null;
  }

  const alt = image?.alternateText ?? "";
  return (
    <figure className="flex h-full min-h-[18rem] flex-col overflow-hidden rounded-[1.75rem] border border-[#C9D6FF] bg-white p-2.5 shadow-[0_26px_70px_rgba(33,68,255,0.12)] sm:min-h-[23rem] sm:p-3 lg:min-h-[30rem]">
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-[1.35rem] border border-[#E3E8FF] bg-[#F8FAFF]">
        <Image
          alt={alt}
          className="h-full w-full object-cover"
          decoding="async"
          height={image?.height ?? undefined}
          priority
          sizes={image?.sizes ?? "(min-width: 1024px) 46vw, 100vw"}
          src={src}
          srcSet={image?.srcSet ?? undefined}
          style={getImageStyle(image?.focalPoint)}
          width={image?.width ?? undefined}
        />
      </div>
      {image?.caption ? (
        <figcaption className="px-1 pt-3 text-xs leading-5 text-[#4B5568]">
          {image.caption}
        </figcaption>
      ) : null}
    </figure>
  );
};

export const ResourceHighlightBlock = (props: IResourceHighlightBlockProps) => {
  const actions = [...(props.link ? [props.link] : []), ...(props.buttons ?? [])];
  const hasImage = Boolean(props.image?.defaultSrc);
  const details = [
    ["Resource type", props.resourceType],
    ["Category", props.category],
    ["Reading time", props.readingTime],
    ["Download", props.downloadLabel],
  ].filter((detail): detail is [string, string] => Boolean(detail[1]));
  const hasContent = Boolean(
    props.eyebrow ||
    props.heading ||
    props.subheading ||
    hasImage ||
    props.tags?.length ||
    details.length ||
    actions.length,
  );

  if (!hasContent) return null;

  return (
    <KaajSectionShell
      id={props.anchorId ?? undefined}
      className={props.parentClass}
      spacing={props.spacing}
    >
      {props.eyebrow ? <KaajHeading align="left" eyebrow={props.eyebrow} /> : null}

      <KaajCard
        className={cn(
          "relative isolate grid overflow-hidden",
          hasImage ? "lg:grid-cols-[minmax(0,0.92fr)_minmax(380px,1.08fr)]" : "mx-auto max-w-4xl",
        )}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-8 top-0 z-10 h-px bg-gradient-to-r from-transparent via-[#8FA3FF] to-transparent"
        />

        <div
          className={cn(
            "min-w-0 p-6 sm:p-8 lg:flex lg:flex-col lg:justify-center lg:p-12 xl:p-14",
            !hasImage && "lg:p-14",
          )}
        >
          {props.heading && (
            <h2 className="kaaj-section-heading text-[#151927]">{props.heading}</h2>
          )}

          {props.subheading && (
            <RichTextHtml
              className="mt-5 max-w-[62ch] text-pretty text-base leading-8 text-[#4B5568] [overflow-wrap:anywhere] md:text-lg"
              html={props.subheading}
            />
          )}

          {details.length > 0 ? (
            <dl className="mt-6 grid gap-3 sm:grid-cols-2">
              {details.map(([label, value]) => (
                <ResourceDetail key={label} label={label} value={value} />
              ))}
            </dl>
          ) : null}

          {props.tags && props.tags.length > 0 && (
            <ul aria-label="Resource tags" className="mt-6 flex flex-wrap gap-2">
              {props.tags.map((tag) => (
                <li key={tag.tag}>
                  <span className="inline-flex rounded-full border border-[#D9E1FF] bg-white px-3 py-1 text-sm font-semibold text-[#4B5568] shadow-[0_8px_18px_rgba(33,68,255,0.06)]">
                    {tag.tag}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <KaajActions
            actions={actions}
            className={cn(
              "mt-8 max-[520px]:flex-col max-[520px]:items-stretch max-[520px]:[&>a]:w-full",
            )}
          />
        </div>

        {hasImage ? (
          <div
            className={cn(
              "relative flex min-h-[21rem] items-stretch border-t border-[#D9E1FF] bg-[#F8FAFF] p-4 sm:min-h-[25rem] sm:p-6 lg:min-h-[34rem] lg:border-l lg:border-t-0 lg:p-8",
            )}
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(33,68,255,0.10),transparent_30%),radial-gradient(circle_at_80%_80%,rgba(255,234,249,0.74),transparent_34%)]" />
            <div className="relative z-[1] w-full">
              <ResourceMediaFrame {...props} />
            </div>
          </div>
        ) : null}
      </KaajCard>
    </KaajSectionShell>
  );
};

ResourceHighlightBlock.displayName = "Block::ResourceHighlightBlock";
